import { Action } from 'redux';

import { AppState } from '~src/bootstrap/redux/state/AppState';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { EntityActionNames, IEntityAction } from '~src/spacesPlatform/components/ActionsMenu/EntityActions/IEntityAction';
import { selectSpaceInfo } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';
import { isAdminProfileUsersSpace } from '~src/spacesPlatform/state/SpaceInfo';
import { IServerUserInfo } from '~src/userManagement/users/models/IServerUserInfo';

export const ADMIN_PROFILE_REMOVE_FROM_USER = 'ADMIN_PROFILE_REMOVE_FROM_USER';

/** Per-site info for a system user assigned to an admin profile. */
export interface SelectedSystemUserSiteInfo {
  luid: string;
  siteName: string;
}

/** Minimal system-user info needed by admin profile user flows. */
export interface SelectedSystemUser {
  /** Site-scoped luid for the current/primary site. */
  luid: string;
  id: string;
  /** Server-level user ID for getUsersSiteRoles and other server user APIs. */
  systemUserId: string;
  name: string;
  username: string;
  /** Sites where this user is currently assigned to the profile. */
  siteInfos: SelectedSystemUserSiteInfo[];
}

export interface AdminProfileRemoveFromUserActionPayload extends Action {
  type: typeof ADMIN_PROFILE_REMOVE_FROM_USER;
  profileId: string;
  profileName: string;
  users: SelectedSystemUser[];
}

export function adminProfileRemoveFromUser(profileId: string, profileName: string, users: SelectedSystemUser[]): AdminProfileRemoveFromUserActionPayload {
  return {
    type: ADMIN_PROFILE_REMOVE_FROM_USER,
    profileId,
    profileName,
    users,
  };
}

/**
 * Removes the admin profile assignment from the selected user(s). Only visible in Admin Profile Users tab.
 * API implementation pending - currently a no-op.
 */
export const AdminProfileRemoveFromUserAction: IEntityAction<IServerUserInfo, {}> = {
  name: EntityActionNames.AdminProfileRemoveFromUser,
  translateName: Messages.adminProfileUsersRemoveFromUser,
  isVisible: (_targetItems: IServerUserInfo[], state: AppState) => {
    return isAdminProfileUsersSpace(state.spaceInfo);
  },
  isClickable: (_availableActions: {}, targetItems: IServerUserInfo[]) => {
    return targetItems.length > 0;
  },
  createReduxAction: (targetItems: IServerUserInfo[]) => {
    const state = getAppStore().getState();
    const spaceInfo = selectSpaceInfo(state);
    const profileId = spaceInfo?.item?.id ?? '';
    const profileName = spaceInfo?.item?.name ?? '';
    const users: SelectedSystemUser[] = targetItems.map(item => {
      const luids: string[] = (item as any).luids ?? [item.luid];
      const siteNames: string[] = (item as any).siteNames ?? [];
      return {
        luid: item.luid,
        id: item.id,
        systemUserId: (item as any).systemUserId,
        name: item.name,
        username: item.username,
        siteInfos: luids.map((luid, i) => ({ luid, siteName: siteNames[i] ?? '' })),
      };
    });
    return adminProfileRemoveFromUser(profileId, profileName, users);
  },
};
